import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Plus, Search, X, Download, Send, BadgeCheck, Building2 } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { superAdminApi } from '../../api/superAdmin.js';
import { MonthPager, currentMonthTaipei } from '../../components/SuperAdmin/MonthPager.jsx';
import { overlayDismiss } from '../../lib/overlayDismiss';

/**
 * /admin/minutes — 灌入分鐘數（super_admin）
 * 手動灌分鐘給商用帳號（不需訂單、沒有線上金流）→ 線下收款後標記核銷；可依經銷商一次統繳。
 * 資料一次拿完（GET /saas-admin/minute-packs 回 packs + accounts），月份篩選 / 分組 / 統計都在這裡算。
 */
const TZ = 8 * 3600 * 1000;
const dayOf = (ms) => new Date(ms + TZ).toISOString().slice(0, 10);
const timeOf = (ms) => new Date(ms + TZ).toISOString().slice(0, 16).replace('T', ' ');
const monthOf = (ms) => dayOf(ms).slice(0, 7);
const num = (n) => Number(n || 0).toLocaleString();
const SYM = { TWD: 'NT$', USD: 'US$', THB: '฿' };
const money = (n, cur) => `${SYM[cur] || cur} ${num(n)}`;
const DIRECT = '_direct';   // 沒有歸屬經銷商的直客

// portal 到 body：祖先只要有 transform / backdrop-filter（進場動畫、毛玻璃頁首），position:fixed 就會改以祖先定位
function Modal(props) {
    return createPortal(<ModalBody {...props} />, document.body);
}

function ModalBody({ title, onClose, children, footer, wide = false }) {
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4" {...overlayDismiss(onClose)}>
            <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] flex flex-col rounded-xl bg-cream-50 border border-cream-200 shadow-xl`} role="dialog" aria-modal="true">
                <div className="px-6 py-4 flex items-center border-b border-cream-200">
                    <h2 className="heading-display text-lg font-semibold text-ink-900">{title}</h2>
                    <button onClick={onClose} aria-label="close" className="ml-auto p-1 rounded text-ink-400 hover:text-ink-700 hover:bg-cream-100 focus-visible:outline-2 focus-visible:outline-accent-500"><X size={16} /></button>
                </div>
                <div className="px-6 py-4 overflow-y-auto">{children}</div>
                {footer && <div className="px-6 py-3 border-t border-cream-200 flex items-center justify-end gap-2 flex-wrap">{footer}</div>}
            </div>
        </div>
    );
}

function StatusBadge({ p, t }) {
    const map = {
        voided: ['bg-cream-200 text-ink-500', t('minutes.status.voided')],
        gift: ['bg-accent-50 text-accent-700', t('minutes.status.gift')],
        settled: ['bg-success-500/15 text-success-500', t('minutes.status.settled')],
        pending: ['bg-warn-100 text-warn-500', t('minutes.status.pending')],
    };
    const [cls, label] = map[p.voided ? 'voided' : p.settleStatus] || map.pending;
    return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${cls}`}>{label}</span>;
}

export default function LoadMinutesPage() {
    const { t } = useTranslation('superAdmin');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [scope, setScope] = useState('month');             // month | all
    const [month, setMonth] = useState(currentMonthTaipei());
    const [q, setQ] = useState('');
    const [range, setRange] = useState(6);                   // 圖表：近 N 個月
    const [modal, setModal] = useState(null);                // { type:'load'|'settle'|'batch'|'detail', ... }
    const [tick, setTick] = useState(0);
    const reload = () => setTick((n) => n + 1);

    useEffect(() => {
        let cancelled = false;
        superAdminApi.minutePacks()
            .then((d) => { if (!cancelled) { setData(d); setError(null); } })
            .catch((e) => { if (!cancelled) setError(e); });
        return () => { cancelled = true; };
    }, [tick]);

    const accounts = data?.accounts || {};
    const fx = data?.fxTwd || 32;
    const packs = data?.packs || [];
    const acct = (uid) => accounts[uid] || { uid, email: uid, name: null, role: 'business', dealerUid: null };
    // 金額統一換成台幣來加總：台幣訂單用原值，其他幣別用灌入當下存的美金 × 匯率
    const twd = (p) => (p.currency === 'TWD' ? (p.refAmount || 0) : Math.round((p.refAmountUsd || 0) * fx));
    const counted = (p) => !p.voided;                        // 作廢的不進任何統計
    const billable = (p) => counted(p) && p.settleStatus !== 'gift';

    const stats = useMemo(() => {
        const cur = currentMonthTaipei();
        const inMonth = packs.filter((p) => counted(p) && monthOf(p.loadedAt) === cur);
        return {
            minutes: inMonth.reduce((n, p) => n + p.minutes, 0),
            count: inMonth.length,
            amount: inMonth.filter(billable).reduce((n, p) => n + twd(p), 0),
            pending: packs.filter((p) => counted(p) && p.settleStatus === 'pending').reduce((n, p) => n + twd(p), 0),
        };
    }, [packs, fx]);

    const series = useMemo(() => {
        const out = [];
        const [y, m] = currentMonthTaipei().split('-').map(Number);
        for (let i = range - 1; i >= 0; i--) {
            const d = new Date(Date.UTC(y, m - 1 - i, 1));
            const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
            const ps = packs.filter((p) => billable(p) && monthOf(p.loadedAt) === key);
            out.push({ month: key, amount: ps.reduce((n, p) => n + twd(p), 0), minutes: ps.reduce((n, p) => n + p.minutes, 0), count: ps.length });
        }
        return out;
    }, [packs, range, fx]);

    // 篩選 → 依經銷商分組 → 再依入帳帳號分組
    const groups = useMemo(() => {
        const kw = q.trim().toLowerCase();
        const rows = packs.filter((p) => {
            if (scope === 'month' && monthOf(p.loadedAt) !== month) return false;
            if (!kw) return true;
            const a = acct(p.uid); const o = acct(p.orderedByUid);
            return [p.orderNo, p.paymentNo, a.email, a.name, o.email].some((x) => String(x || '').toLowerCase().includes(kw));
        });
        const byDealer = new Map();
        for (const p of rows) {
            const dealerUid = acct(p.uid).dealerUid || DIRECT;
            if (!byDealer.has(dealerUid)) byDealer.set(dealerUid, new Map());
            const byAcct = byDealer.get(dealerUid);
            if (!byAcct.has(p.uid)) byAcct.set(p.uid, []);
            byAcct.get(p.uid).push(p);
        }
        return [...byDealer.entries()].map(([dealerUid, byAcct]) => ({
            dealerUid, dealer: dealerUid === DIRECT ? null : acct(dealerUid),
            accounts: [...byAcct.entries()].map(([uid, ps]) => ({ uid, account: acct(uid), packs: ps })),
            all: [...byAcct.values()].flat(),
        }));
    }, [packs, scope, month, q, accounts]);

    const sum = (ps) => {
        const live = ps.filter(counted);
        const pend = live.filter((p) => p.settleStatus === 'pending');
        return { count: live.length, minutes: live.reduce((n, p) => n + p.minutes, 0), amount: live.filter(billable).reduce((n, p) => n + twd(p), 0), pendCount: pend.length, pendAmount: pend.reduce((n, p) => n + twd(p), 0) };
    };
    // 統繳看的是該經銷商「全部月份」的待核銷，不受上面月份篩選影響
    const pendingOf = (dealerUid) => packs.filter((p) => counted(p) && p.settleStatus === 'pending' && (acct(p.uid).dealerUid || DIRECT) === dealerUid);

    return (
        <div>
            <PageHeader
                eyebrow="super admin · load minutes"
                title={t('minutes.title')}
                description={t('minutes.description')}
                actions={<Button onClick={() => setModal({ type: 'load' })}><Plus size={15} />{t('minutes.load')}</Button>}
            />
            {error && <div className="mb-6 px-4 py-3 rounded-md border border-danger-500/30 bg-danger-100/40 text-sm text-danger-500">{String(error.message || error)}</div>}

            <div className="mb-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-cream-200 border-y border-cream-200">
                {[[num(stats.minutes), t('minutes.stat.minutes')], [num(stats.count), t('minutes.stat.count')], [money(stats.amount, 'TWD'), t('minutes.stat.amount')], [money(stats.pending, 'TWD'), t('minutes.stat.pending')]].map(([v, label]) => (
                    <div key={label} className="px-5 py-4 first:pl-1">
                        <div className="tnum heading-display text-2xl font-semibold text-ink-900 leading-none">{v}</div>
                        <div className="mt-1.5 text-[11px] text-ink-400">{label}</div>
                    </div>
                ))}
            </div>
            <p className="-mt-6 mb-6 text-right text-[11px] text-ink-400">{t('minutes.fxNote', { fx })}</p>

            <Card className="mb-10">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <div className="text-sm font-medium text-ink-900">{t('minutes.chart.title')}</div>
                        <div className="text-[11px] text-ink-400">{t('minutes.chart.desc')}</div>
                    </div>
                    <div className="inline-flex rounded-md border border-cream-300 overflow-hidden text-xs">
                        {[6, 12].map((n) => (
                            <button key={n} onClick={() => setRange(n)} className={`px-3 py-1.5 ${range === n ? 'bg-ink-900 text-cream-50' : 'text-ink-700 hover:bg-cream-100'}`}>{t('minutes.chart.range', { n })}</button>
                        ))}
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={series} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                        <CartesianGrid stroke="var(--color-cream-200)" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `NT$ ${num(v)}`} width={90} />
                        <Tooltip formatter={(v, _k, item) => [`NT$ ${num(v)}（${num(item.payload.minutes)} ${t('minutes.unit')}・${item.payload.count} ${t('minutes.rows')}）`, t('minutes.chart.revenue')]} />
                        <Area isAnimationActive={false} type="monotone" dataKey="amount" stroke="var(--color-accent-500)" fill="var(--color-accent-500)" fillOpacity={0.12} strokeWidth={2} dot={{ r: 3 }} />
                    </AreaChart>
                </ResponsiveContainer>
            </Card>

            <div className="flex items-center gap-3 flex-wrap mb-3">
                <h2 className="heading-display text-lg font-semibold text-ink-900">{t('minutes.records')}</h2>
                <div className="inline-flex rounded-full border border-cream-300 overflow-hidden text-xs">
                    {['month', 'all'].map((s) => (
                        <button key={s} onClick={() => setScope(s)} className={`px-3 py-1 ${scope === s ? 'bg-ink-900 text-cream-50' : 'text-ink-700 hover:bg-cream-100'}`}>{t(`minutes.scope.${s}`)}</button>
                    ))}
                </div>
                {scope === 'month' && <MonthPager month={month} onChange={setMonth} />}
                <label className="ml-auto relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('minutes.search')} className="pl-8 pr-3 py-1.5 w-64 max-w-full bg-white border border-cream-300 rounded-md text-xs focus:outline-none focus:border-accent-500" />
                </label>
            </div>

            {!data ? (
                <div className="text-center py-16 text-ink-400 font-mono text-xs">Loading…</div>
            ) : groups.length === 0 ? (
                <p className="py-14 text-center text-sm text-ink-400 italic">{t('minutes.empty')}</p>
            ) : groups.map((g) => {
                const gs = sum(g.all);
                const pend = pendingOf(g.dealerUid);
                return (
                    <Card key={g.dealerUid} padded={false} className="mb-5 overflow-hidden">
                        <div className="px-4 py-3 bg-cream-100/70 flex items-center gap-3 flex-wrap">
                            <span className="font-medium text-ink-900">{g.dealer ? (g.dealer.name || g.dealer.email) : t('minutes.direct')}</span>
                            {g.dealer && <span className="text-xs text-ink-500">{g.dealer.email}</span>}
                            {g.dealer && <span className="px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 text-[10px]">{t('role.dealer')}</span>}
                            <span className="ml-auto text-xs text-ink-500 tnum">
                                {t('minutes.groupSum', { count: gs.count, minutes: num(gs.minutes), amount: money(gs.amount, 'TWD') })}
                                {pend.length > 0 && <span className="ml-2 text-warn-500">{t('minutes.groupPending', { count: pend.length, amount: money(pend.reduce((n, p) => n + twd(p), 0), 'TWD') })}</span>}
                            </span>
                            {pend.length > 0 && <Button size="sm" variant="outline" onClick={() => setModal({ type: 'batch', group: g, pending: pend })}><BadgeCheck size={13} />{t('minutes.batch')}</Button>}
                        </div>
                        {g.accounts.map(({ uid, account, packs: ps }) => {
                            const as = sum(ps);
                            return (
                                <div key={uid} className="border-t border-cream-200">
                                    <div className="px-4 py-2 flex items-center gap-2 flex-wrap text-xs bg-cream-50">
                                        <Building2 size={13} className="text-ink-300" />
                                        <span className="font-medium text-ink-900">{account.name || account.email}</span>
                                        <span className="px-1.5 py-0.5 rounded bg-danger-100/60 text-danger-500 text-[10px]">{t('role.business')}</span>
                                        <span className="text-ink-400 font-mono">{account.email}</span>
                                        {account.billingMode !== 'prepaid' && <span className="px-1.5 py-0.5 rounded bg-warn-100 text-warn-500 text-[10px]" title={t('minutes.postpaidHint')}>{t('minutes.postpaid')}</span>}
                                        <span className="ml-auto text-ink-500 tnum">{t('minutes.groupSum', { count: as.count, minutes: num(as.minutes), amount: money(as.amount, 'TWD') })}</span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        {/* 固定欄寬：每個帳號各一張表，不固定的話各表欄位會對不齊 */}
                                        <table className="w-full min-w-[820px] table-fixed text-xs">
                                            <colgroup><col className="w-[19%]" /><col className="w-[25%]" /><col className="w-[13%]" /><col className="w-[12%]" /><col className="w-[15%]" /><col className="w-[16%]" /></colgroup>
                                            <tbody>
                                                {ps.map((p) => (
                                                    <tr key={p.orderNo} className={`border-t border-cream-100 ${p.voided ? 'opacity-50' : ''}`}>
                                                        <td className="px-4 py-2.5 whitespace-nowrap"><div className="font-mono font-medium text-ink-900">{p.orderNo}</div><div className="text-[11px] text-ink-400 tnum">{timeOf(p.loadedAt)}</div></td>
                                                        <td className="px-2 py-2.5"><div className="text-ink-900 truncate" title={acct(p.orderedByUid).email}>{acct(p.orderedByUid).email}</div><div className="text-[11px] text-ink-400">{p.orderedByUid === p.uid ? t('minutes.sameAccount') : `→ ${account.email}`}</div></td>
                                                        <td className="px-2 py-2.5 text-right tnum font-medium text-ink-900 whitespace-nowrap">{num(p.minutes)}<div className="text-[11px] font-normal text-ink-400">{t('minutes.until', { day: p.expiryDay || '—' })}</div></td>
                                                        <td className="px-2 py-2.5 text-right tnum whitespace-nowrap">{p.settleStatus === 'gift' ? <span className="text-ink-400">—</span> : <><div className="font-medium text-ink-900">{money(p.refAmount, p.currency)}</div><div className="text-[11px] text-ink-400">US$ {num(p.refAmountUsd)}</div></>}</td>
                                                        <td className="px-2 py-2.5 whitespace-nowrap"><StatusBadge p={p} t={t} />{p.paymentNo && <div className="mt-0.5 text-[11px] text-ink-400 font-mono">{t('minutes.payment')} {p.paymentNo}</div>}</td>
                                                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                                            {!p.voided && p.settleStatus === 'pending' && <Button size="sm" variant="outline" className="mr-2" onClick={() => setModal({ type: 'settle', pack: p })}>{t('minutes.markSettled')}</Button>}
                                                            <Button size="sm" variant="ghost" onClick={() => setModal({ type: 'detail', pack: p })}>{t('minutes.detail')}</Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })}
                    </Card>
                );
            })}

            {modal?.type === 'load' && <LoadModal t={t} accounts={accounts} fx={fx} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
            {modal?.type === 'settle' && <SettleModal t={t} pack={modal.pack} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
            {modal?.type === 'batch' && <BatchModal t={t} group={modal.group} pending={modal.pending} acct={acct} twd={twd} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
            {modal?.type === 'detail' && <DetailModal t={t} pack={modal.pack} acct={acct} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
        </div>
    );
}

function ErrorLine({ err }) {
    return err ? <p className="mt-3 text-xs text-danger-500" role="alert">{String(err.message || err)}</p> : null;
}

function LoadModal({ t, accounts, fx, onClose, onDone }) {
    const list = useMemo(() => Object.values(accounts).filter((a) => a.role === 'business').sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email))), [accounts]);
    // 預設 = 隔年同月同日（與後端 minuteQuota.expiryDayOf 同規則）
    const defaultExpiry = useMemo(() => {
        const today = dayOf(Date.now());
        const md = today.slice(5);
        return `${Number(today.slice(0, 4)) + 1}-${md === '02-29' ? '02-28' : md}`;
    }, []);
    const [uid, setUid] = useState('');
    const [minutes, setMinutes] = useState('');
    const [amount, setAmount] = useState('');
    const [expiryDay, setExpiryDay] = useState(defaultExpiry);
    const [note, setNote] = useState('');
    const [switchPrepaid, setSwitchPrepaid] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const a = accounts[uid];
    const n = Number(minutes);
    const valid = uid && Number.isInteger(n) && n > 0 && expiryDay >= dayOf(Date.now());
    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            if (switchPrepaid && a?.billingMode !== 'prepaid') await superAdminApi.setBillingMode(uid, { billingMode: 'prepaid' });
            await superAdminApi.loadMinutes({ uid, minutes: n, refAmount: amount === '' ? null : Number(amount), currency: 'TWD', expiryDay, note: note || null });
            onDone();
        } catch (e) { setErr(e); setBusy(false); }
    };
    return (
        <Modal title={t('minutes.loadModal.title')} onClose={onClose} footer={<>
            <Button variant="outline" onClick={onClose}>{t('minutes.cancel')}</Button>
            <Button disabled={!valid || busy} onClick={submit}>{t('minutes.loadModal.submit')}</Button>
        </>}>
            <div className="space-y-4">
                <label className="block">
                    <span className="block text-xs font-medium text-ink-700 mb-1.5">{t('minutes.loadModal.account')}</span>
                    <select value={uid} onChange={(e) => { setUid(e.target.value); setSwitchPrepaid(false); }} className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500">
                        <option value="">{t('minutes.loadModal.pick')}</option>
                        {list.map((x) => <option key={x.uid} value={x.uid}>{x.name ? `${x.name}・${x.email}` : x.email}</option>)}
                    </select>
                </label>
                {a && a.billingMode !== 'prepaid' && (
                    <div className="px-3 py-2.5 rounded-md border border-warn-500/40 bg-warn-100/60 text-xs text-ink-700 leading-relaxed">
                        {t('minutes.loadModal.postpaidWarn')}
                        <label className="mt-2 flex items-start gap-2 font-medium text-ink-900">
                            <input type="checkbox" className="mt-0.5" checked={switchPrepaid} onChange={(e) => setSwitchPrepaid(e.target.checked)} />
                            {t('minutes.loadModal.switchPrepaid')}
                        </label>
                    </div>
                )}
                {a && !a.hasCsSeats && <p className="text-xs text-warn-500">{t('minutes.loadModal.noSeats')}</p>}
                <Input label={t('minutes.loadModal.minutes')} type="number" min="1" step="1" inputMode="numeric" placeholder="1000" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                <Input label={t('minutes.loadModal.amount')} type="number" min="0" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} hint={t('minutes.loadModal.amountHint', { fx })} />
                <Input label={t('minutes.loadModal.expiry')} type="date" min={dayOf(Date.now())} value={expiryDay} onChange={(e) => setExpiryDay(e.target.value)} hint={t('minutes.loadModal.expiryHint')} />
                <Input label={t('minutes.loadModal.note')} placeholder={t('minutes.loadModal.notePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />
                <p className="px-3 py-2.5 rounded-md bg-cream-100 text-xs text-ink-700 leading-relaxed">{t('minutes.loadModal.notice')}</p>
            </div>
            <ErrorLine err={err} />
        </Modal>
    );
}

function SettleModal({ t, pack, onClose, onDone }) {
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const submit = async () => {
        setBusy(true); setErr(null);
        try { await superAdminApi.patchMinutePack(pack.orderNo, { settleStatus: 'settled', settleNote: note || null }); onDone(); }
        catch (e) { setErr(e); setBusy(false); }
    };
    return (
        <Modal title={t('minutes.settleModal.title', { orderNo: pack.orderNo })} onClose={onClose} footer={<>
            <Button variant="outline" onClick={onClose}>{t('minutes.cancel')}</Button>
            <Button disabled={busy} onClick={submit}><BadgeCheck size={14} />{t('minutes.settleModal.submit')}</Button>
        </>}>
            <p className="mb-4 text-sm text-ink-700">{num(pack.minutes)} {t('minutes.unit')}・{money(pack.refAmount, pack.currency)}</p>
            <Input label={t('minutes.settleNote')} placeholder={t('minutes.settleNotePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
            <ErrorLine err={err} />
        </Modal>
    );
}

function BatchModal({ t, group, pending, acct, twd, onClose, onDone }) {
    const [picked, setPicked] = useState(() => new Set(pending.map((p) => p.orderNo)));
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);
    const chosen = pending.filter((p) => picked.has(p.orderNo));
    const total = chosen.reduce((n, p) => n + twd(p), 0);
    const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const who = group.dealer ? group.dealer.email : (chosen[0] ? acct(chosen[0].uid).email : '');

    const downloadXlsx = () => {
        const rows = chosen.map((p) => ({
            [t('minutes.col.order')]: p.orderNo, [t('minutes.col.loadedAt')]: timeOf(p.loadedAt),
            [t('minutes.col.company')]: acct(p.uid).name || '', [t('minutes.col.account')]: acct(p.uid).email,
            [t('minutes.col.minutes')]: p.minutes, [t('minutes.col.expiry')]: p.expiryDay || '',
            [t('minutes.col.currency')]: p.currency, [t('minutes.col.amount')]: p.refAmount || 0, [t('minutes.col.amountUsd')]: p.refAmountUsd || 0,
            [t('minutes.col.note')]: p.note || '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.sheet_add_aoa(ws, [[], [t('minutes.batchModal.total'), '', '', '', chosen.reduce((n, p) => n + p.minutes, 0), '', 'TWD', total]], { origin: -1 });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, t('minutes.batchModal.sheet'));
        XLSX.writeFile(wb, `${t('minutes.batchModal.sheet')}_${who.replace(/[^\w.@-]/g, '_')}_${dayOf(Date.now())}.xlsx`);
    };
    const run = async (fn) => { setBusy(true); setErr(null); setMsg(null); try { await fn(); } catch (e) { setErr(e); } finally { setBusy(false); } };
    const sendMail = () => run(async () => {
        const toUid = group.dealer ? group.dealer.uid : chosen[0].uid;
        const r = await superAdminApi.emailMinuteStatement({ toUid, orderNos: chosen.map((p) => p.orderNo) });
        setMsg(t('minutes.batchModal.sent', { email: r.to }));
    });
    const settle = () => run(async () => {
        await superAdminApi.settleMinutePayment({ orderNos: chosen.map((p) => p.orderNo), note: note || null });
        onDone();
    });

    return (
        <Modal wide title={t('minutes.batchModal.title', { who })} onClose={onClose} footer={<>
            <Button variant="outline" onClick={onClose}>{t('minutes.close')}</Button>
            <Button variant="outline" disabled={!chosen.length || busy} onClick={downloadXlsx}><Download size={14} />{t('minutes.batchModal.download')}</Button>
            <Button variant="outline" disabled={!chosen.length || busy} onClick={sendMail}><Send size={14} />{t('minutes.batchModal.email')}</Button>
            <Button disabled={!chosen.length || busy} onClick={settle}><BadgeCheck size={14} />{t('minutes.batchModal.settle')}</Button>
        </>}>
            <div className="flex items-baseline gap-3 flex-wrap mb-3">
                <span className="tnum heading-display text-2xl font-semibold text-ink-900">{money(total, 'TWD')}</span>
                <span className="text-xs text-ink-500">{t('minutes.batchModal.picked', { n: chosen.length, total: pending.length })}</span>
                <button className="ml-auto text-xs text-accent-700 hover:underline" onClick={() => setPicked(new Set(chosen.length ? [] : pending.map((p) => p.orderNo)))}>
                    {chosen.length ? t('minutes.batchModal.none') : t('minutes.batchModal.all')}
                </button>
            </div>
            <div className="border border-cream-200 rounded-md divide-y divide-cream-100 bg-white">
                {pending.map((p) => (
                    <label key={p.orderNo} className="flex items-center gap-3 px-3 py-2.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={picked.has(p.orderNo)} onChange={() => toggle(p.orderNo)} />
                        <span className="min-w-0"><span className="block font-mono font-medium text-ink-900">{p.orderNo}</span><span className="block text-[11px] text-ink-400">{t('minutes.loadedOn', { day: dayOf(p.loadedAt) })}</span></span>
                        <span className="min-w-0 truncate text-ink-700">{acct(p.uid).name || acct(p.uid).email}・{num(p.minutes)} {t('minutes.unit')}</span>
                        <span className="ml-auto text-right tnum whitespace-nowrap"><span className="block font-medium text-ink-900">{money(p.refAmount, p.currency)}</span><span className="block text-[11px] text-ink-400">US$ {num(p.refAmountUsd)}</span></span>
                    </label>
                ))}
            </div>
            <p className="mt-2 mb-4 text-[11px] text-ink-400">{t('minutes.batchModal.hint')}</p>
            <Input label={t('minutes.settleNote')} placeholder={t('minutes.settleNotePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
            {msg && <p className="mt-3 text-xs text-success-500" role="status">{msg}</p>}
            <ErrorLine err={err} />
        </Modal>
    );
}

function DetailModal({ t, pack: p, acct, onClose, onDone }) {
    const [confirmVoid, setConfirmVoid] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const a = acct(p.uid); const o = acct(p.orderedByUid);
    const dealer = a.dealerUid && a.dealerUid !== a.uid ? acct(a.dealerUid) : null;
    const act = async (body) => { setBusy(true); setErr(null); try { await superAdminApi.patchMinutePack(p.orderNo, body); onDone(); } catch (e) { setErr(e); setBusy(false); } };
    const rows = [
        [t('minutes.d.status'), <StatusBadge key="s" p={p} t={t} />],
        [t('minutes.d.loadedAt'), timeOf(p.loadedAt)],
        [t('minutes.d.company'), a.name || '—'],
        [t('minutes.d.orderedBy'), `${o.email}（${t(`role.${o.role}`, { defaultValue: o.role })}）`],
        [t('minutes.d.account'), a.email],
        [t('minutes.d.dealer'), dealer ? dealer.email : '—'],
        [t('minutes.d.minutes'), `${num(p.minutes)} ${t('minutes.unit')}`],
        [t('minutes.d.expiry'), p.expiryDay || '—'],
        [t('minutes.d.amount'), p.settleStatus === 'gift' ? t('minutes.status.gift') : `${money(p.refAmount, p.currency)}（US$ ${num(p.refAmountUsd)}）`],
        [t('minutes.d.note'), p.note || '—'],
        [t('minutes.d.settle'), p.settleStatus === 'settled' ? [t('minutes.status.settled'), p.settledAt && dayOf(p.settledAt), p.paymentNo, p.settleNote].filter(Boolean).join('・') : '—'],
    ];
    return (
        <Modal title={t('minutes.d.title', { orderNo: p.orderNo })} onClose={onClose} footer={confirmVoid ? (
            // 作廢確認：說明 + 取消 / 確定作廢，兩顆同尺寸；其他按鈕先收起來，避免誤按
            <>
                <span className="mr-auto text-sm text-danger-500">{t('minutes.d.voidConfirm')}</span>
                <Button variant="outline" disabled={busy} onClick={() => setConfirmVoid(false)}>{t('minutes.cancel')}</Button>
                <Button variant="danger" className="border border-transparent" disabled={busy} onClick={() => act({ voided: true })}>{t('minutes.d.voidYes')}</Button>
            </>
        ) : (
            <>
                {!p.voided && <Button variant="ghost" className="mr-auto !text-danger-500 hover:!bg-danger-100/40" onClick={() => setConfirmVoid(true)}>{t('minutes.d.void')}</Button>}
                {!p.voided && p.settleStatus === 'settled' && <Button variant="outline" disabled={busy} onClick={() => act({ settleStatus: 'pending' })}>{t('minutes.d.unsettle')}</Button>}
                <Button variant="outline" onClick={onClose}>{t('minutes.close')}</Button>
            </>
        )}>
            <dl className="divide-y divide-cream-200 text-sm">
                {rows.map(([k, v]) => (
                    <div key={k} className="py-2.5 flex items-center justify-between gap-4"><dt className="text-ink-400 shrink-0">{k}</dt><dd className="text-ink-900 text-right break-all">{v}</dd></div>
                ))}
            </dl>
            <div className="mt-4 text-xs font-medium text-ink-900">{t('minutes.d.events')}</div>
            <ul className="mt-2 space-y-1.5 text-xs">
                {(p.events || []).length === 0 && <li className="text-ink-400 italic">{t('minutes.d.noEvents')}</li>}
                {(p.events || []).map((e, i) => (
                    <li key={i} className="flex gap-3"><span className="font-mono text-ink-400 tnum whitespace-nowrap">{timeOf(e.at)}</span><span className="text-ink-700">{t(`minutes.event.${e.type}`, { by: e.by, minutes: num(e.minutes), paymentNo: e.paymentNo || '', note: e.note || '', defaultValue: e.type })}</span></li>
                ))}
            </ul>
            <ErrorLine err={err} />
        </Modal>
    );
}
